// Admin User Management — detail + block/unblock modal.
//
// Driven by `open={selectedUserId !== null}`; lazily loads the full user
// record (useAdminUserDetail is `enabled` only when an id is selected) and
// renders it grouped into Account / Profile / Standing / Wallet / Holdings
// / Recent sessions cards. The block controls sit at the bottom: a
// reason+duration form when the user is unblocked, or the standing block +
// an Unblock button when they are. Admins cannot be blocked — the form is
// disabled with a hint, matching the server's BAD_REQUEST guard.

import { useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@greenhold/components";

import { formatCents } from "../marketplace/feeMath";
import { useAdminUserDetail, type UserDetail } from "./useAdminUserDetail";
import { useBlockUser } from "./useBlockUser";
import { useUnblockUser } from "./useUnblockUser";

const REASON_MIN = 5;
const REASON_MAX = 500;

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide opacity-60">{label}</span>
      <span className="text-sm">{value ?? "—"}</span>
    </div>
  );
}

export function UserDetailDialog({
  selectedUserId,
  onClose,
}: {
  selectedUserId: string | null;
  onClose: () => void;
}) {
  const query = useAdminUserDetail(selectedUserId);

  return (
    <Dialog
      open={selectedUserId !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto max-w-3xl">
        {selectedUserId === null ? null : query.isPending ? (
          <>
            <DialogHeader>
              <DialogTitle>Loading user…</DialogTitle>
              <DialogDescription>Fetching the account record.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4 rounded" />
              <Skeleton className="h-4 w-full rounded" />
              <Skeleton className="h-4 w-2/3 rounded" />
              <Skeleton className="h-24 w-full rounded" />
            </div>
          </>
        ) : query.isError ? (
          <>
            <DialogHeader>
              <DialogTitle>Couldn't load user</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-destructive">{query.error.message}</p>
            <Button variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          </>
        ) : (
          <UserDetailBody user={query.data.data} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserDetailBody({ user }: { user: UserDetail }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{user.display_name?.trim() || user.email}</DialogTitle>
        <DialogDescription>
          {user.handle ? `@${user.handle} · ` : ""}
          {user.email}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 text-sm">
        {/* Account */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Account</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Field
              label="Email"
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="break-all">{user.email}</span>
                  {user.email_verified ? (
                    <Badge variant="success">Verified</Badge>
                  ) : (
                    <Badge variant="warning">Unverified</Badge>
                  )}
                </span>
              }
            />
            <Field
              label="User id"
              value={<span className="font-mono text-xs break-all">{user.id}</span>}
            />
            <Field label="Signed up" value={fmtDateTime(user.created_at)} />
            <Field label="Last sign in" value={fmtDateTime(user.last_sign_in_at)} />
            <Field
              label="Admin"
              value={user.is_admin ? <Badge>Admin</Badge> : "No"}
            />
          </CardContent>
        </Card>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Field label="Display name" value={user.display_name} />
            <Field label="Handle" value={user.handle ? `@${user.handle}` : "—"} />
            <Field label="First name" value={user.first_name} />
            <Field label="Last name" value={user.last_name} />
            <Field label="Region" value={user.region} />
            <Field label="Language" value={user.language} />
            <Field label="Nationality" value={user.nationality} />
            <div className="col-span-2">
              <Field
                label="Bio"
                value={
                  user.bio ? (
                    <span className="whitespace-pre-wrap">{user.bio}</span>
                  ) : (
                    "—"
                  )
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Standing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Standing</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Field label="Creator status" value={user.creator_status} />
            <Field label="KYC tier" value={user.kyc_tier} />
          </CardContent>
        </Card>

        {/* Wallet */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Wallet</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <Field label="Balance" value={formatCents(user.balance_cents)} />
            <Field label="Escrow" value={formatCents(user.escrow_cents)} />
            <Field
              label="Cumulative resale"
              value={formatCents(user.cumulative_resale_cents)}
            />
          </CardContent>
        </Card>

        {/* Holdings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Holdings</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Field label="Certificates" value={user.collection_count} />
            <Field label="Sealed dossiers (vault)" value={user.sealed_dossier_count} />
            <Field label="Active share listings" value={user.active_share_listings} />
            <Field label="Active sealed listings" value={user.active_sealed_listings} />
          </CardContent>
        </Card>

        {/* Recent sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent sessions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {user.recent_sessions.length === 0 ? (
              <p className="p-4 opacity-60">No sessions on record.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-brand-paper-100 text-left">
                  <tr>
                    <th className="px-4 py-2">Created</th>
                    <th className="px-4 py-2">Last seen</th>
                    <th className="px-4 py-2">Expires</th>
                    <th className="px-4 py-2">Revoked?</th>
                  </tr>
                </thead>
                <tbody>
                  {user.recent_sessions.map((s) => (
                    <tr key={s.id} className="border-t border-brand-paper-200">
                      <td className="px-4 py-2">{fmtDateTime(s.created_at)}</td>
                      <td className="px-4 py-2">{fmtDateTime(s.last_seen_at)}</td>
                      <td className="px-4 py-2">{fmtDateTime(s.expires_at)}</td>
                      <td className="px-4 py-2">
                        {s.revoked_at ? (
                          <Badge variant="destructive">
                            {fmtDateTime(s.revoked_at)}
                          </Badge>
                        ) : (
                          <span className="opacity-60">Active</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Block controls */}
        {user.is_blocked ? (
          <BlockedPanel user={user} />
        ) : (
          <BlockForm user={user} />
        )}
      </div>
    </>
  );
}

function BlockForm({ user }: { user: UserDetail }) {
  const block = useBlockUser(user.id);
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState<"permanent" | "timed">("permanent");
  const [until, setUntil] = useState("");

  const trimmedLen = reason.trim().length;
  const reasonValid = trimmedLen >= REASON_MIN && trimmedLen <= REASON_MAX;
  const untilValid =
    duration === "permanent" ||
    (until !== "" && new Date(until).getTime() > Date.now());
  const canSubmit =
    !user.is_admin && reasonValid && untilValid && !block.isPending;

  async function submit() {
    if (!canSubmit) return;
    try {
      const res = await block.mutateAsync({
        reason: reason.trim(),
        until:
          duration === "timed" && until
            ? new Date(until).toISOString()
            : null,
      });
      toast.success(
        `User blocked. ${res.data.sessions_revoked} session(s) revoked.`
      );
      setReason("");
    } catch {
      // error surfaced inline below via block.error
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Block user</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {user.is_admin ? (
          <p className="text-sm text-destructive">Admins cannot be blocked.</p>
        ) : null}

        <label className="block text-sm">
          <span className="block mb-1 opacity-70">
            Reason ({REASON_MIN}–{REASON_MAX} chars)
          </span>
          <textarea
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            rows={3}
            value={reason}
            maxLength={REASON_MAX}
            disabled={user.is_admin}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        <fieldset className="space-y-2" disabled={user.is_admin}>
          <legend className="text-sm opacity-70 mb-1">Duration</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`block-duration-${user.id}`}
              checked={duration === "permanent"}
              onChange={() => setDuration("permanent")}
            />
            Permanent
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`block-duration-${user.id}`}
              checked={duration === "timed"}
              onChange={() => setDuration("timed")}
            />
            Until…
            <input
              type="datetime-local"
              className="border rounded px-2 py-1 bg-white disabled:opacity-50"
              value={until}
              disabled={duration !== "timed"}
              onChange={(e) => setUntil(e.target.value)}
            />
          </label>
          {duration === "timed" && until !== "" && !untilValid ? (
            <p className="text-xs text-destructive">
              The lift time must be in the future.
            </p>
          ) : null}
        </fieldset>

        {block.isError ? (
          <p className="text-sm text-destructive">{block.error.message}</p>
        ) : null}

        <div className="flex justify-end">
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {block.isPending ? "Blocking…" : "Block user"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BlockedPanel({ user }: { user: UserDetail }) {
  const unblock = useUnblockUser(user.id);

  async function submit() {
    try {
      const res = await unblock.mutateAsync();
      toast.success(
        res.data.was_blocked ? "User unblocked." : "User was not blocked."
      );
    } catch {
      // error surfaced inline below
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          <span className="inline-flex items-center gap-2">
            Currently blocked
            <Badge variant="destructive">
              {user.blocked_until
                ? `until ${fmtDateTime(user.blocked_until)}`
                : "Permanent"}
            </Badge>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Reason" value={user.block_reason} />
          <Field label="Blocked at" value={fmtDateTime(user.blocked_at)} />
          <Field
            label="Lifts"
            value={
              user.blocked_until ? fmtDateTime(user.blocked_until) : "Permanent"
            }
          />
          <Field
            label="Blocked by"
            value={
              user.blocked_by ? (
                <span className="font-mono text-xs break-all">
                  {user.blocked_by}
                </span>
              ) : (
                "—"
              )
            }
          />
        </div>

        {unblock.isError ? (
          <p className="text-sm text-destructive">{unblock.error.message}</p>
        ) : null}

        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={submit}
            disabled={unblock.isPending}
          >
            {unblock.isPending ? "Unblocking…" : "Unblock"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
